import DefaultFormJson from "@/DefaultForm.json";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "@/components/ui/use-toast";
import { useFetchDataV2 } from "@/hooks/useFetchDataV2";
import usePostData from "@/hooks/usePostData";
import type { Step } from "@/pages/parameters/ParametersForms";
import {
  buildInitializePayloadFromDraft,
  hydrateDraftSteps,
  type ProjectTypeOption,
} from "./CallSpecificFormBuilder";
import CallSpecificFormBuilder from "./CallSpecificFormBuilder";
import {
  AlertTriangle,
  ArrowRightLeft,
  FileStack,
  FolderKanban,
  Layers3,
  Loader2,
  RefreshCw,
  SquarePen,
} from "lucide-react";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  buildInitializePayloadFromSchema,
} from "./GenerateNewLink";

const API_URL = import.meta.env.VITE_API_ENDPOINT;
const DEFAULT_FORM: Step[] = DefaultFormJson as Step[];

type LibraryItem = {
  scope: "project_type" | "call";
  projectTypeId?: number;
  callId?: number;
  preservedCallId?: number | null;
  programId?: number | null;
  name: string;
  sourceDeleted?: boolean;
  chercheurFond?: boolean | null;
};

type CallFormActionDialogProps = {
  callId: number;
  programName: string;
  formMode?: "global" | "call_specific";
  canChangeForm: boolean;
  trigger?: ReactNode;
};

type FormStrategy = "existing_model" | "call_specific";

type SourceField = {
  name: string;
  label: string;
  type: string;
  required?: boolean;
  options?: unknown;
  nativeField?: string | null;
  step?: number;
};

type SourceSection = {
  name: string;
  label: string;
  description?: string | null;
  step?: number;
  fields?: SourceField[];
};

type SourceStep = {
  id?: number;
  name: string;
  label: string;
  description?: string | null;
  options?: unknown;
  step?: number;
  fields?: SourceField[];
  sections?: SourceSection[];
};

const asArray = <T,>(value: any): T[] => {
  if (Array.isArray(value)) return value as T[];
  if (value && Array.isArray(value["hydra:member"])) {
    return value["hydra:member"] as T[];
  }
  return [];
};

const getLibraryItemKey = (item: LibraryItem) =>
  item.scope === "project_type"
    ? `project_type-${item.projectTypeId ?? 0}`
    : `call-${item.callId ?? item.preservedCallId ?? 0}`;

const isSameLibraryItem = (left: LibraryItem | null, right: LibraryItem) => {
  if (!left) return false;
  return getLibraryItemKey(left) === getLibraryItemKey(right);
};

export default function CallFormActionDialog({
  callId,
  programName,
  formMode = "global",
  canChangeForm: _canChangeForm,
  trigger,
}: CallFormActionDialogProps) {
  const { t } = useTranslation("dashboard", { keyPrefix: "soustraitant" });
  const navigate = useNavigate();
  const { fetchData } = useFetchDataV2("");
  const projectTypes = useFetchDataV2<any[]>(
    `${API_URL}/project-types`,
    `project-types-call-form-action-${callId}`,
    1000 * 60 * 30,
  );
  const { postData } = usePostData();

  const [open, setOpen] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [confirmRiskOpen, setConfirmRiskOpen] = useState(false);
  const [isCheckingChange, setIsCheckingChange] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [formStrategy, setFormStrategy] = useState<FormStrategy | null>(null);
  const [selectedModel, setSelectedModel] = useState<LibraryItem | null>(null);
  const [projectTypeForms, setProjectTypeForms] = useState<LibraryItem[]>([]);
  const [callForms, setCallForms] = useState<LibraryItem[]>([]);
  const [callSpecificDraftSteps, setCallSpecificDraftSteps] = useState<Step[]>([]);
  const [callSpecificSelectedStep, setCallSpecificSelectedStep] = useState(1);
  const [callProjectTypeId, setCallProjectTypeId] = useState<number>(0);
  const [callProjectCount, setCallProjectCount] = useState<number>(0);
  const [callChercheurFond, setCallChercheurFond] = useState<boolean | null>(null);

  const projectTypeOptions = useMemo<ProjectTypeOption[]>(
    () =>
      asArray<any>(projectTypes.data)
        .map((type: any) => ({
          id: Number(type?.id),
          label: String(type?.name ?? `Type ${type?.id ?? ""}`).trim(),
        }))
        .filter((item) => Number.isFinite(item.id) && item.id > 0 && item.label !== ""),
    [projectTypes.data],
  );

  useEffect(() => {
    if (formStrategy === "call_specific" && callSpecificDraftSteps.length === 0) {
      setCallSpecificDraftSteps(hydrateDraftSteps(DEFAULT_FORM));
      setCallSpecificSelectedStep(1);
    }
  }, [callSpecificDraftSteps.length, formStrategy]);

  const existingModelAvailable =
    projectTypeForms.length > 0 || callForms.length > 0;

  const isStepTwoValid =
    formStrategy === "call_specific"
      ? callSpecificDraftSteps.length > 0
      : formStrategy === "existing_model" && selectedModel !== null;

  const loadFormsLibrary = async () => {
    const formsLibrary = await fetchData(`${API_URL}/field-forms/library`);
    setProjectTypeForms(
      Array.isArray(formsLibrary?.projectTypeForms)
        ? formsLibrary.projectTypeForms
        : [],
    );
    setCallForms(Array.isArray(formsLibrary?.callForms) ? formsLibrary.callForms : []);
  };

  const resetWizard = () => {
    setWizardOpen(false);
    setIsApplying(false);
    setFormStrategy(null);
    setSelectedModel(null);
    setCallSpecificDraftSteps([]);
    setCallSpecificSelectedStep(1);
  };

  const openChangeWizard = async () => {
    await loadFormsLibrary();
    setOpen(false);
    setWizardOpen(true);
  };

  const openEditCurrentForm = () => {
    setOpen(false);
    if (formMode === "call_specific") {
      navigate(
        `/dashboard/parameters?params=2&callId=${callId}&programName=${encodeURIComponent(
          programName,
        )}`,
      );
      return;
    }

    navigate(
      `/dashboard/parameters?params=2&globalCallWarning=1&programName=${encodeURIComponent(
        programName,
      )}`,
    );
  };

  const handleOpenChangeWizard = async () => {
    setIsCheckingChange(true);
    try {
      const state = await fetchData(`${API_URL}/call-for-project/form-state?callId=${callId}`);
      const projectCount = Number(state?.projectCount ?? 0);
      setCallProjectTypeId(Number(state?.projectTypeId ?? 0));
      setCallProjectCount(Number.isFinite(projectCount) ? projectCount : 0);
      setCallChercheurFond(
        typeof state?.chercheurFond === "boolean" ? state.chercheurFond : null,
      );

      if (projectCount > 0) {
        setOpen(false);
        setConfirmRiskOpen(true);
        return;
      }

      await openChangeWizard();
    } catch (_error) {
      toast({
        title: "Erreur",
        description:
          "Impossible de vérifier l'état de l'appel pour le moment. Veuillez réessayer.",
        duration: 5000,
      });
    } finally {
      setIsCheckingChange(false);
    }
  };

  const handleApplyChange = async () => {
    if (!isStepTwoValid) return;

    setIsApplying(true);
    try {
      let payload: { steps: any[] } | null = null;

      if (formStrategy === "existing_model" && selectedModel) {
        const query = new URLSearchParams();
        if (selectedModel.scope === "project_type" && selectedModel.projectTypeId) {
          query.set("projectTypeId", String(selectedModel.projectTypeId));
        }
        if (selectedModel.scope === "call" && selectedModel.callId) {
          query.set("callId", String(selectedModel.callId));
        }
        if (selectedModel.scope === "call" && selectedModel.preservedCallId) {
          query.set("preservedCallId", String(selectedModel.preservedCallId));
        }

        const sourceSchema = await fetchData(
          `${API_URL}/field-forms/global?${query.toString()}`,
        );
        if (!Array.isArray(sourceSchema) || sourceSchema.length === 0) {
          throw new Error("Modèle source introuvable.");
        }

        payload = buildInitializePayloadFromSchema(sourceSchema as SourceStep[]);
      }

      if (formStrategy === "call_specific") {
        payload = buildInitializePayloadFromDraft(callSpecificDraftSteps);
      }

      if (!payload) {
        throw new Error("Aucun modèle n'a été préparé pour le remplacement.");
      }

      const replaceResult = await postData({
        url: `${API_URL}/field_forms/replace-call-model`,
        data: {
          callId,
          steps: payload.steps,
          modelSource:
            callProjectTypeId > 0
              ? { scope: "project_type", projectTypeId: Number(callProjectTypeId) }
              : formStrategy === "existing_model" &&
                    selectedModel?.scope === "project_type" &&
                    selectedModel.projectTypeId
                ? { scope: "project_type", projectTypeId: Number(selectedModel.projectTypeId) }
                : { scope: "call" },
        },
      });

      if (!replaceResult?.success) {
        throw new Error(
          "Le formulaire de l'appel n'a pas pu être remplacé.",
        );
      }

      const refreshedState = await fetchData(
        `${API_URL}/call-for-project/form-state?callId=${callId}`,
      );
      const refreshedSchema = await fetchData(
        `${API_URL}/field-forms/global?callId=${callId}`,
      );

      if (
        !refreshedState?.useCallSpecificForm ||
        !Array.isArray(refreshedSchema) ||
        refreshedSchema.length === 0
      ) {
        throw new Error(
          "Le nouveau formulaire n'a pas pu être activé correctement pour cet appel.",
        );
      }

      toast({
        title: "Formulaire remplacé",
        description:
          formStrategy === "call_specific"
            ? "Le nouvel éditeur dédié a été appliqué à cet appel."
            : "Le modèle sélectionné a été appliqué à cet appel.",
        duration: 4500,
      });

      window.location.href = `/dashboard/parameters?params=2&callId=${callId}&programName=${encodeURIComponent(
        programName,
      )}`;
    } catch (error: any) {
      toast({
        title: "Erreur",
        description:
          error?.message ||
          "Le formulaire n'a pas pu être remplacé. Veuillez réessayer.",
        duration: 5000,
      });
    } finally {
      setIsApplying(false);
    }
  };

  return (
    <>
      {trigger ? (
        <div
          onClick={(e) => {
            e.stopPropagation();
            setOpen(true);
          }}
        >
          {trigger}
        </div>
      ) : (
        <Button
          onClick={(e) => {
            e.stopPropagation();
            setOpen(true);
          }}
          className="rounded-full flex bg-gray-300 mr-2 hover:bg-gray-300 py-2 px-4 text-xs text-gray-800"
        >
          <RefreshCw className="w-4 h-4 text-gray-700 mr-2" />
          {t("Modifier le formulaire")}
        </Button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[860px]">
          <DialogHeader>
            <DialogTitle>Gérer le formulaire de cet appel</DialogTitle>
            <DialogDescription>
              Vous pouvez modifier le formulaire actuel ou le remplacer par un autre
              modèle.
            </DialogDescription>
          </DialogHeader>

          <div className="rounded-2xl border border-[#d7e5d4] bg-[linear-gradient(135deg,#f7fbf6_0%,#eef6ec_100%)] p-5">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#1d5214]">
              Modèle actuellement utilisé
            </p>
            <div className="mt-3 flex flex-wrap items-start justify-between gap-4">
              <div>
                <h3 className="text-lg font-semibold text-[#1d5214]">
                  {formMode === "call_specific"
                    ? "Formulaire spécifique à cet appel"
                    : "Formulaire global"}
                </h3>
                <p className="mt-2 max-w-2xl text-sm leading-6 text-[#4c6f46]">
                  {formMode === "call_specific"
                    ? `Cet appel utilise actuellement un formulaire dédié au programme "${programName}".`
                    : "Cet appel utilise actuellement le formulaire global du porteur de projet."}
                </p>
              </div>
              <span
                className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                  formMode === "call_specific"
                    ? "bg-[#e7f2e4] text-[#1d5214]"
                    : "bg-slate-200 text-slate-700"
                }`}
              >
                {formMode === "call_specific" ? "Modèle dédié" : "Modèle global"}
              </span>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <button
              type="button"
              onClick={openEditCurrentForm}
              className="rounded-[24px] border border-slate-200 bg-white p-6 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:border-[#1d5214] hover:shadow-[0_18px_35px_rgba(29,82,20,0.12)]"
            >
              <div className="mb-5 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-[#eef6ec] text-[#1d5214]">
                <SquarePen className="h-7 w-7" />
              </div>
              <h3 className="text-xl font-semibold text-[#1d5214]">
                {t("Modifier le formulaire")}
              </h3>
              <p className="mt-3 text-sm leading-6 text-slate-600">
                Conserver la structure actuelle et ajuster ses champs, sections
                ou étapes.
              </p>
            </button>

            <button
              type="button"
              onClick={() => void handleOpenChangeWizard()}
              disabled={isCheckingChange}
              className="rounded-[24px] border border-slate-200 bg-white p-6 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:border-[#1d5214] hover:shadow-[0_18px_35px_rgba(29,82,20,0.12)]"
            >
              <div className="mb-5 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-[#f8f3e8] text-[#9a6700]">
                {isCheckingChange ? (
                  <Loader2 className="h-7 w-7 animate-spin" />
                ) : (
                  <ArrowRightLeft className="h-7 w-7" />
                )}
              </div>
              <div>
                <h3 className="text-xl font-semibold text-slate-900">
                  Changer de formulaire
                </h3>
                <p className="mt-3 text-sm leading-6 text-slate-600">
                  Remplacer le formulaire actuel par un autre modèle ou créer
                  un nouveau formulaire dédié.
                </p>
              </div>
            </button>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={wizardOpen} onOpenChange={(nextOpen) => !nextOpen && resetWizard()}>
        <DialogContent className="sm:max-w-[1200px]">
          <DialogHeader>
            <DialogTitle>Changer le formulaire de cet appel</DialogTitle>
            <DialogDescription>
              Sélectionnez un modèle existant ou créez un nouveau formulaire dédié.
              Le formulaire actuel sera remplacé.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            <div className="grid gap-4 md:grid-cols-2">
              <button
                type="button"
                disabled={!existingModelAvailable}
                onClick={() => {
                  setFormStrategy("existing_model");
                  setSelectedModel(null);
                }}
                className={`rounded-[24px] border p-6 text-left shadow-sm transition-all ${
                  existingModelAvailable
                    ? formStrategy === "existing_model"
                      ? "border-[#1d5214] bg-[#f7fbf6] shadow-[0_18px_35px_rgba(29,82,20,0.12)]"
                      : "border-slate-200 bg-white hover:-translate-y-0.5 hover:border-[#1d5214]"
                    : "cursor-not-allowed border-slate-200 bg-slate-100 opacity-60"
                }`}
              >
                <div className="mb-5 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-[#eef6ec] text-[#1d5214]">
                  <FileStack className="h-7 w-7" />
                </div>
                <h3 className="text-xl font-semibold text-[#1d5214]">
                  Utiliser un modèle existant
                </h3>
                <p className="mt-3 text-sm leading-6 text-slate-600">
                  Réutiliser un formulaire déjà configuré et le dupliquer sur cet appel.
                </p>
              </button>

              <button
                type="button"
                onClick={() => {
                  setFormStrategy("call_specific");
                  setSelectedModel(null);
                }}
                className={`rounded-[24px] border p-6 text-left shadow-sm transition-all ${
                  formStrategy === "call_specific"
                    ? "border-[#1d5214] bg-[#f7fbf6] shadow-[0_18px_35px_rgba(29,82,20,0.12)]"
                    : "border-slate-200 bg-white hover:-translate-y-0.5 hover:border-[#1d5214]"
                }`}
              >
                <div className="mb-5 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-[#eef6ec] text-[#1d5214]">
                  <FolderKanban className="h-7 w-7" />
                </div>
                <h3 className="text-xl font-semibold text-[#1d5214]">
                  Créer un nouveau formulaire dédié
                </h3>
                <p className="mt-3 text-sm leading-6 text-slate-600">
                  Construire ici un nouveau formulaire spécifique à cet appel.
                </p>
              </button>
            </div>

            {formStrategy === "existing_model" && (
              <div className="space-y-6">
                <section className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700">
                      <Layers3 className="h-5 w-5" />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-slate-900">
                        Modèles par types de projet
                      </h2>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
                    {projectTypeForms.map((item) => (
                      <button
                        key={`type-${item.projectTypeId}`}
                        type="button"
                        onClick={() => setSelectedModel(item)}
                        className={`group flex min-h-[132px] flex-col justify-start rounded-2xl border p-4 text-left shadow-sm transition-all ${
                          selectedModel?.scope === "project_type" &&
                          selectedModel?.projectTypeId === item.projectTypeId
                            ? "border-[#1d5214] bg-[#f7fbf6] shadow-[0_18px_35px_rgba(29,82,20,0.12)]"
                            : "border-slate-200 bg-white hover:-translate-y-0.5 hover:border-emerald-300"
                        }`}
                      >
                        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-emerald-600">
                          Type de projet
                        </p>
                        <h3 className="mt-3 break-words text-base font-semibold leading-6 text-slate-900">
                          {item.name}
                        </h3>
                      </button>
                    ))}
                  </div>
                </section>

                <section className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-lime-100 text-lime-700">
                      <FolderKanban className="h-5 w-5" />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-slate-900">
                        Modèles d'appel à projet
                      </h2>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
                    {callForms
                      .filter((item) => Number(item.callId) !== callId)
                      .map((item) => (
                        <button
                          key={getLibraryItemKey(item)}
                          type="button"
                          onClick={() => setSelectedModel(item)}
                          className={`group flex min-h-[132px] flex-col justify-start rounded-2xl border p-4 text-left shadow-sm transition-all ${
                            isSameLibraryItem(selectedModel, item)
                              ? "border-[#1d5214] bg-[#f7fbf6] shadow-[0_18px_35px_rgba(29,82,20,0.12)]"
                              : "border-slate-200 bg-white hover:-translate-y-0.5 hover:border-lime-300"
                          }`}
                        >
                          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-lime-600">
                            {item.sourceDeleted ? "Appel supprimé" : "Appel à projet"}
                          </p>
                          <h3 className="mt-3 break-words text-base font-semibold leading-6 text-slate-900">
                            {item.name}
                          </h3>
                          {item.sourceDeleted ? (
                            <p className="mt-2 text-xs leading-5 text-amber-700">
                              Modèle conservé car déjà utilisé par un ou plusieurs projets.
                            </p>
                          ) : null}
                        </button>
                      ))}
                  </div>
                </section>
              </div>
            )}

            {formStrategy === "call_specific" && (
              <div className="space-y-5">
                <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm leading-6 text-amber-900">
                  Vous remplacez le formulaire actuel par une nouvelle structure.
                  Pensez à valider seulement si vous confirmez ce changement.
                </div>
                <CallSpecificFormBuilder
                  steps={callSpecificDraftSteps}
                  selectedStep={callSpecificSelectedStep}
                  setSelectedStep={setCallSpecificSelectedStep}
                  setSteps={setCallSpecificDraftSteps}
                  projectTypeOptions={projectTypeOptions}
                  projectTypeOptionsLoading={projectTypes.loading}
                  allowAdvancedScoringModes={true}
                />
              </div>
            )}
          </div>

          <DialogFooter className="gap-2 sm:justify-between">
            <Button type="button" variant="outline" onClick={resetWizard}>
              Annuler
            </Button>
            <Button
              type="button"
              onClick={() => void handleApplyChange()}
              disabled={!isStepTwoValid || isApplying}
              className="bg-[#1d5214] text-white hover:bg-[#174010]"
            >
              {isApplying ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Application en cours
                </>
              ) : (
                "Appliquer ce formulaire"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmRiskOpen} onOpenChange={setConfirmRiskOpen}>
        <DialogContent className="sm:max-w-[560px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="h-5 w-5" />
              Avertissement - Risque majeur de perte de données
            </DialogTitle>
            <DialogDescription>
              {callProjectCount > 1
                ? `${callProjectCount} projets ont déjà été créés dans cet appel.`
                : "Un projet a déjà été créé dans cet appel."}
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-xl border border-red-300 bg-red-50 p-4 text-sm leading-6 text-red-900">
            <p>
              Attention : Des projets ayant déjà été déposés dans le cadre du présent appel à projets, toute modification du formulaire est susceptible d’altérer la cohérence des données déjà soumises et de compromettre l’intégrité, la traçabilité ainsi que l’opposabilité des informations enregistrées. Etes-vous sûr de vouloir continuer ?
            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setConfirmRiskOpen(false);
                setOpen(true);
              }}
            >
              Annuler
            </Button>
            <Button
              type="button"
              className="bg-[#1d5214] text-white hover:bg-[#174010]"
              onClick={() => {
                setConfirmRiskOpen(false);
                void openChangeWizard();
              }}
            >
              Poursuivre le changement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

